import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, DotDotDotIcon, PrivateIcon, PublicIcon, Spinner, } from '@streamr/tv-ui';
import { useEffect, useRef } from 'react';
import { keyframes, styled } from 'styled-components';
import { BroadcastStateService } from '../services/broadcaststate/BroadcastStateService';
import { RECORDING_LINK_PARAM, RecordingPlaybackService, } from '../services/recording/RecordingPlaybackService';
import { RecordingService } from '../services/recording/RecordingService';
import { formatHowLongAgo } from '../utils/dateFormatters';
import { formBroadcastUrl } from '../utils/url';
import { Anchor } from './Anchor';
import { showRecordingOptionsPopup } from './popups/recording/RecordingOptionsPopup';
import { showRecordingPlayerPopup } from './popups/recording/RecordingPlayerPopup';
export const RecordingsList = ({ recordings, status, variant, hasMore, onLoadMore, }) => {
    return (_jsxs(Root, { children: [(status === 'loaded' || status === 'loadingMore') &&
                recordings.length > 0 && (_jsx(RecordingsListGrid, { children: recordings.map((recording) => (_jsx(RecordingListItem, { recording: recording, variant: variant }, recording.recordingId))) })), status === 'loaded' && recordings.length === 0 && (_jsx(NoRecordings, { children: "No recordings found for this broadcast" })), status === 'error' && (_jsx(NoRecordings, { children: "An error occured while fetching the recordings. Please try again later." })), (status === 'loading' || status === 'loadingMore') && (_jsx(SpinnerContainer, { children: _jsx(StyledSpinner, {}) })), hasMore && status !== 'error' && (_jsx(LoadMoreContainer, { children: _jsx(LoadMoreButton, { type: "button", onClick: onLoadMore, disabled: status === 'loadingMore', children: "Load more" }) }))] }));
};
/** Where the recording opens by itself (without the key of a private one). */
function formRecordingLink(recording, broadcast) {
    const url = new URL(formBroadcastUrl(broadcast, '/recordings'));
    url.searchParams.set(RECORDING_LINK_PARAM, recording.recordingId);
    return url.toString();
}
function formatDuration(durationUs) {
    const seconds = Math.round(durationUs / 1_000_000);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const two = (value) => String(value).padStart(2, '0');
    return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
}
const RecordingListItem = ({ recording, variant, }) => {
    const broadcast = BroadcastStateService.getOutput().useRequiredActiveBroadcast();
    const thumbnail = RecordingPlaybackService.getOutput().useThumbnail(recording.recordingId);
    // the thumbnail is encrypted like the recording: the service opens it
    useEffect(() => {
        RecordingPlaybackService.getNotificationInterface().notifyLoadThumbnail(recording, broadcast);
    }, [recording, broadcast]);
    return (_jsxs(Item, { tag: "a", href: formRecordingLink(recording, broadcast), "data-automation-id": "recording-item", onClick: (event) => {
            // the link is for "open in a new tab"; a click plays here
            event.preventDefault();
            showRecordingPlayerPopup({ recording, broadcast });
        }, children: [_jsxs(ItemMedia, { children: [(thumbnail === undefined ||
                        thumbnail.status === 'loading') && _jsx(ItemSkeleton, {}), thumbnail?.status === 'none' && (_jsx(NoImage, { children: "Image not available" })), thumbnail?.status === 'loaded' && (_jsx(ItemImage, { src: thumbnail.url, alt: "", "data-loaded": "true" }))] }), variant === 'viewer' ? (_jsx(ItemDate, { children: formatHowLongAgo(new Date(recording.timestamp)) })) : (_jsxs(ItemAccess, { children: [recording.isPublic ? _jsx(PublicIcon, {}) : _jsx(PrivateIcon, {}), _jsx(ItemAccessText, { children: recording.isPublic ? 'Public' : 'Private' })] })), recording.durationUs > 0 && (_jsxs(ItemDuration, { children: [formatDuration(recording.durationUs), recording.partCount > 1 &&
                        ` · ${recording.partCount} parts`] })), _jsx(RecordingOptionsPopupTrigger, { recording: recording, variant: variant })] }));
};
const RecordingOptionsPopupTrigger = ({ recording, variant, }) => {
    const anchorRef = useRef(null);
    const broadcast = BroadcastStateService.getOutput().useRequiredActiveBroadcast();
    return (_jsx(PositionedAnchor, { ref: anchorRef, children: _jsx(ItemButton, { type: "button", "data-automation-id": "recording-options", onClick: (event) => {
                event.preventDefault();
                event.stopPropagation();
                showRecordingOptionsPopup({
                    anchorRef,
                    variant: 'desktop',
                    recording,
                    broadcast,
                    onDelete: variant === 'broadcaster'
                        ? () => {
                            RecordingService.getNotificationInterface().notifyDeleteRecording(recording);
                        }
                        : undefined,
                });
            }, children: _jsx(DotDotDotIcon, {}) }) }));
};
const Root = styled.div `
    width: 100%;
    display: inline-flex;
    flex-direction: column;
    gap: 1rem;
`;
const NoRecordings = styled.div `
    margin: 1rem 0;
`;
const RecordingsListGrid = styled.div `
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(1, 1fr);
    @media (min-width: 40rem) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 60rem) {
        grid-template-columns: repeat(3, 1fr);
    }
`;
const Item = styled(Button) `
    position: relative;
    overflow: hidden;
    border-radius: 1.5rem;
    --Button_HoverTransform: scale(1.01);
    --Button_ActiveTransform: scale(1.01);
    color: var(--tv-ui-color-3);
    width: 100%;
`;
const ItemImage = styled.img.attrs({ draggable: false }) `
    width: 100%;
    height: 100%;
    object-fit: cover;
    -webkit-user-drag: none;
    user-select: none;
    -webkit-user-select: none;
    opacity: 0;
    transition: opacity 0.25s ease;

    &[data-loaded='true'] {
        opacity: 1;
    }
`;
const ItemMedia = styled.div `
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    `;
const NoImage = styled.div `
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 1.5rem;
    background-color: var(--tv-ui-color-15);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    box-sizing: border-box;
    font-weight: 400;
    font-size: 0.875rem;
    color: var(--tv-ui-color-16);
`;
const shimmer = keyframes `
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;
const ItemSkeleton = styled.div `
    position: absolute;
    inset: 0;
    background: linear-gradient(
        90deg,
        var(--tv-ui-color-15) 25%,
        var(--tv-ui-color-14) 37%,
        var(--tv-ui-color-15) 63%
    );
    background-size: 400% 100%;
    animation: ${shimmer} 2.5s linear infinite;
`;
const ItemDate = styled.span `
    position: absolute;
    top: 0;
    left: 0;
    background: var(--tv-ui-color-15);
    backdrop-filter: blur(3rem);
    border-radius: 1rem;
    padding: 0.25rem 0.5rem;
    margin: 1rem;
`;
const ItemAccessText = styled.div `
    margin-left: 0;
    white-space: nowrap;
    overflow: hidden;
    max-width: 0;
    opacity: 0;
    transition: max-width 0.2s ease, margin-left 0.2s ease, opacity 0.2s ease;
    position: relative;
`;
const ItemAccess = styled.div `
    position: absolute;
    top: 0;
    left: 0;
    background: var(--tv-ui-color-15);
    backdrop-filter: blur(3rem);
    margin: 1rem;
    padding: 0.5rem;
    border-radius: 1rem;
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    min-width: 2rem;
    max-height: 2rem;
    box-sizing: border-box;

    &:hover {
        ${ItemAccessText} {
            max-width: 20ch;
            margin-left: 0.5rem;
            opacity: 1;
        }
    }
`;
const ItemButton = styled(Button) `
    padding: 0.5rem;
    border-radius: 1rem;
    backdrop-filter: blur(3rem);
    --Button_HoverTransform: scale(1.05);
    --Button_ActiveTransform: scale(1.05);
    --Button_Background: var(--tv-ui-color-15);
    --Button_ActiveBackground: var(--tv-ui-color-14);
    color: var(--tv-ui-color-3);
`;
const PositionedAnchor = styled(Anchor) `
    position: absolute;
    top: 0;
    right: 0;
    margin: 1rem;
    display: inline-flex;
`;
const SpinnerContainer = styled.div `
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 0;
`;
const StyledSpinner = styled(Spinner) `
    --Spinner_Size: 3rem;
    --Spinner_TrackColor: var(--tv-ui-color-2);
    --Spinner_Color: var(--tv-ui-color-3);
    --Spinner_Thickness: 3px;
`;
const LoadMoreContainer = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
`;
const LoadMoreButton = styled(Button) `
    --Button_HoverTransform: scale(1.05);
    --Button_ActiveTransform: scale(1.05);
    --Button_Background: var(--tv-ui-color-15);
    --Button_ActiveBackground: var(--tv-ui-color-15);
    --Button_Padding: 1rem 2rem;
    --Button_BorderRadius: 1rem;
    color: var(--tv-ui-color-3);
`;
const ItemDuration = styled.span `
    position: absolute;
    bottom: 0;
    right: 0;
    background: var(--tv-ui-color-15);
    backdrop-filter: blur(3rem);
    border-radius: 1rem;
    padding: 0.25rem 0.5rem;
    margin: 1rem;
    font-variant-numeric: tabular-nums;
`;
